import { useQueryClient } from "@tanstack/react-query";
import cn from "classnames";
import { toastServerError } from "helpers";
import { useBoolean } from "hooks";
// Deep paths, not the `modules/aiAgents` barrel: see AIConversations.tsx.
import {
  AiLessonGuidanceIssue,
  EAiAssistantChannel
} from "modules/aiAgents/aiAgents.types";
import {
  useDraftLesson,
  useLessonMutations
} from "modules/aiAgents/apis/lessons";
import { useGetAssistants } from "modules/aiAgents/apis/useGetAssistants";
import { LessonForm } from "modules/aiAgents/components/LessonForm";
import { AI_LESSON_LIMITS } from "modules/aiAgents/constants/lessons";
import {
  LessonFormValue,
  applyDraftToForm,
  chooseLessonVariant,
  emptyLessonForm,
  isLessonTag,
  parseLessonApiError,
  toCreateLessonBody,
  validateLessonForm
} from "modules/aiAgents/helpers/lessons";
import { ORDER_STATUSES_LIST } from "modules/orders/constants/enums/orderStatus";
import { ReactNode, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";

// Relative, not the `modules/aiReview` barrel: only the query key is needed.
import { AI_REVIEW_QUERY_KEY } from "../../../aiReview/constants/enums";
import {
  AiTurnDetail,
  AiTurnTraceRound,
  ChatMessage
} from "../../aiConversations.types";
import { useGetTurnDetail } from "../../apis/useGetTurnDetail";
import {
  formatFcfa,
  prettyToolInput,
  summariseTurnLiveState,
  toBadReplyText,
  turnFidelityNoticeKey,
  turnInboundMediaTexts,
  turnLiveStateDetails,
  turnMediaFailures,
  turnMessageText,
  turnOutcomeReason,
  turnRawReplyToShow,
  turnSenderLabelKey,
  turnTraceRounds
} from "../../helpers/correction";

import styles from "./CorrectionPanel.module.scss";

interface Props {
  conversationId: string;
  message: ChatMessage;
  onClose: () => void;
  // The backend serving turns and lessons answered 404 (not deployed yet):
  // the caller hides the feature.
  onUnavailable?: () => void;
}

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M2 2l10 10M12 2L2 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

// The same sparkle as the composer's "Improve message with AI".
const SparkleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M11.2 2.6 12.9 7.1 17.4 8.8 12.9 10.5 11.2 15 9.5 10.5 5 8.8 9.5 7.1Z"
      fill="currentColor"
    />
    <path
      d="M4.6 2 5.2 3.6 6.8 4.2 5.2 4.8 4.6 6.4 4 4.8 2.4 4.2 4 3.6Z"
      fill="currentColor"
    />
    <path
      d="M15.4 12.2 15.9 13.5 17.2 14 15.9 14.5 15.4 15.8 14.9 14.5 13.6 14 14.9 13.5Z"
      fill="currentColor"
    />
  </svg>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className={styles.field}>
    <div className={styles.fieldLabel}>{label}</div>
    {children}
  </div>
);

const Chips = ({ items }: { items: string[] }) => (
  <div className={styles.chips}>
    {items.map((item) => (
      <span key={item} className={styles.chip}>
        {item}
      </span>
    ))}
  </div>
);

// The orders page's label for a status (a translation key), or the raw value.
const orderStatusLabel = (status: string) =>
  ORDER_STATUSES_LIST.find((option) => option.value === status)?.label ??
  status;

const formatDay = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

// The live state of that moment: the cart (with its lines when they were
// recorded), the open orders, the delivery days the assistant was given and
// how many saved addresses. Addresses are only counted, never shown.
const TurnLiveState = ({
  liveState
}: {
  liveState: AiTurnDetail["liveState"];
}) => {
  const { t } = useTranslation();
  const state = summariseTurnLiveState(liveState);
  const details = turnLiveStateDetails(liveState);
  if (!state || !details) return null;

  return (
    <Field label={t("aiTurn.state")}>
      <div className={styles.muted}>
        {state.cartItemCount > 0
          ? t("aiTurn.state.cart", { count: state.cartItemCount })
          : t("aiTurn.state.cartEmpty")}
      </div>
      {details.cartLines.map((line, i) => (
        <div key={`${line.label}-${i}`} className={styles.value}>
          {`• ${line.label} × ${line.quantity}`}
          {line.lineTotal !== null ? ` — ${formatFcfa(line.lineTotal)}` : ""}
        </div>
      ))}
      {details.subtotal !== null ? (
        <div className={styles.muted}>
          {t("aiTurn.state.subtotal", {
            amount: formatFcfa(details.subtotal)
          })}
        </div>
      ) : null}

      {details.orders ? (
        details.orders.map((order, i) => {
          const day = order.deliveryDate ? formatDay(order.deliveryDate) : "";
          return (
            <div key={`${order.status}-${i}`} className={styles.muted}>
              {order.isAwaitingPayment
                ? t("aiTurn.state.orderUnpaid", {
                    total: formatFcfa(order.total)
                  })
                : t("aiTurn.state.order", {
                    status: t(orderStatusLabel(order.status)),
                    total: formatFcfa(order.total)
                  })}
              {day ? ` · ${t("aiTurn.state.delivery", { date: day })}` : ""}
            </div>
          );
        })
      ) : (
        <>
          {state.openOrders > 0 ? (
            <div className={styles.muted}>
              {t("aiTurn.state.orders", { count: state.openOrders })}
            </div>
          ) : null}
          {state.awaitingPayment > 0 ? (
            <div className={styles.muted}>
              {t("aiTurn.state.awaitingPayment", {
                count: state.awaitingPayment
              })}
            </div>
          ) : null}
        </>
      )}

      {details.deliveryDays.length > 0 ? (
        <div className={styles.muted}>
          {t("aiTurn.state.deliveryDays", {
            days: details.deliveryDays.map(formatDay).filter(Boolean).join(", ")
          })}
        </div>
      ) : null}

      {details.addressCount !== null ? (
        <div className={styles.muted}>
          {t("aiTurn.state.addresses", { count: details.addressCount })}
        </div>
      ) : null}
    </Field>
  );
};

// One recorded step of the reply: what the model wrote and the tools it used.
const TraceRound = ({ round }: { round: AiTurnTraceRound }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.traceRound}>
      <div className={styles.chips}>
        <span className={styles.fieldLabel}>
          {t("aiTurn.trace.round", { n: round.round + 1 })}
        </span>
        {round.forced ? (
          <span className={styles.chip}>
            {t("aiTurn.trace.forced", { reason: round.forced.reason })}
          </span>
        ) : null}
      </div>

      {round.draftText ? (
        <>
          <div className={styles.muted}>{t("aiTurn.trace.draft")}</div>
          <pre className={styles.tracePre}>{round.draftText}</pre>
        </>
      ) : null}

      {round.tools.length > 0 ? (
        round.tools.map((tool, i) => (
          <div key={`${tool.name}-${i}`} className={styles.field}>
            <div className={styles.chips}>
              <span className={styles.value}>{tool.name}</span>
              {tool.isError ? (
                <span className={styles.badge}>{t("aiTurn.trace.error")}</span>
              ) : null}
            </div>
            {tool.input ? (
              <>
                <div className={styles.muted}>{t("aiTurn.trace.input")}</div>
                <pre className={styles.tracePre}>
                  {prettyToolInput(tool.input)}
                </pre>
              </>
            ) : null}
            {tool.resultPreview ? (
              <>
                <div className={styles.muted}>{t("aiTurn.trace.result")}</div>
                <pre className={styles.tracePre}>{tool.resultPreview}</pre>
              </>
            ) : null}
          </div>
        ))
      ) : (
        <div className={styles.muted}>{t("aiTurn.trace.noTool")}</div>
      )}
    </div>
  );
};

const TurnContext = ({
  turn,
  messageId
}: {
  turn: AiTurnDetail;
  messageId: string;
}) => {
  const { t } = useTranslation();
  const { value: showHistory, toggle: toggleHistory } = useBoolean(false);
  const { value: showTrace, toggle: toggleTrace } = useBoolean(false);
  const { metric } = turn;
  const mediaTexts = turnInboundMediaTexts(turn);
  const mediaFailures = turnMediaFailures(turn);
  const rawReply = turnRawReplyToShow(turn);
  const rounds = turnTraceRounds(turn);
  const traceTruncated = turn.snapshot?.traceTruncated === true;
  const reason = turnOutcomeReason(turn.outcomeReason);
  const contextAttempts = turn.snapshot?.contextAttempts ?? 0;
  const hasMediaFailure = mediaFailures.voice > 0 || mediaFailures.photo > 0;

  return (
    <>
      <div className={styles.notice}>
        <span
          className={cn(styles.badge, {
            [styles.badgeFull]: turn.fidelity === "full"
          })}
        >
          {t(`aiTurn.fidelityBadge.${turn.fidelity}`)}
        </span>
        <span>{t(turnFidelityNoticeKey(turn))}</span>
      </div>
      {turn.snapshotExpired && turn.snapshotRetentionDays ? (
        <div className={styles.muted}>
          {t("aiTurn.snapshotExpired", { days: turn.snapshotRetentionDays })}
        </div>
      ) : null}
      {contextAttempts > 1 ? (
        <div className={styles.muted}>
          {t("aiTurn.contextAttempts", { count: contextAttempts })}
        </div>
      ) : null}

      <Field label={t("aiTurn.customerMessage")}>
        {turn.inbound.map((m) => {
          const media = mediaTexts[m.id];
          return (
            <div key={m.id} className={styles.noteItem}>
              <div className={styles.noteText}>{turnMessageText(m)}</div>
              {media ? (
                <>
                  <div className={styles.muted}>
                    {t(
                      media.kind === "voice"
                        ? "aiTurn.voiceTranscript"
                        : "aiTurn.photoDescription"
                    )}
                  </div>
                  <div className={styles.noteText}>{media.text}</div>
                </>
              ) : null}
            </div>
          );
        })}
      </Field>

      {turn.finalUserText || hasMediaFailure ? (
        <Field label={t("aiTurn.transcript")}>
          {turn.finalUserText ? (
            <div className={styles.noteItem}>
              <div className={styles.noteText}>{turn.finalUserText}</div>
            </div>
          ) : null}
          {mediaFailures.voice > 0 ? (
            <div className={styles.muted}>{t("aiTurn.voiceFailed")}</div>
          ) : null}
          {mediaFailures.photo > 0 ? (
            <div className={styles.muted}>{t("aiTurn.photoFailed")}</div>
          ) : null}
        </Field>
      ) : null}

      <Field label={t("aiTurn.assistantReply")}>
        {turn.replies.map((m) => (
          <div
            key={m.id}
            className={cn(styles.noteItem, styles.replyItem, {
              [styles.replyItemCorrected]: m.id === messageId
            })}
          >
            <div className={styles.noteText}>{turnMessageText(m)}</div>
          </div>
        ))}
      </Field>

      {rawReply ? (
        <Field label={t("aiTurn.rawReply")}>
          <div className={styles.muted}>{t("aiTurn.rawReplyHelp")}</div>
          <div className={styles.noteItem}>
            <div className={styles.noteText}>{rawReply}</div>
          </div>
        </Field>
      ) : null}

      {turn.history.length > 0 ? (
        <div className={styles.field}>
          <button
            type="button"
            className={styles.historyToggle}
            aria-expanded={showHistory}
            onClick={toggleHistory}
          >
            {showHistory ? "▾" : "▸"} {t("aiTurn.earlier")} (
            {turn.history.length})
          </button>
          {showHistory ? (
            <div className={styles.history}>
              {turn.history.map((m) => (
                <div key={m.id} className={styles.historyRow}>
                  <span className={styles.historySender}>
                    {t(turnSenderLabelKey(m.sender))}
                  </span>
                  <span className={styles.noteText}>{turnMessageText(m)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <TurnLiveState liveState={turn.liveState} />

      <Field label={t("aiTurn.result")}>
        {metric ? (
          <div className={styles.value}>
            {t(`aiTurn.outcome.${metric.outcome}`)}
          </div>
        ) : (
          <div className={styles.muted}>{t("aiTurn.noMetric")}</div>
        )}
        {reason?.key ? (
          <div className={styles.muted}>{t(reason.key)}</div>
        ) : null}
        {reason?.detail ? (
          <div className={styles.muted}>
            {reason.key
              ? t("aiTurn.reason.detail", { reason: reason.detail })
              : reason.detail}
          </div>
        ) : null}
      </Field>

      {metric ? (
        <Field label={t("aiTurn.tools")}>
          {metric.toolNames.length > 0 ? (
            <Chips items={metric.toolNames} />
          ) : (
            <div className={styles.muted}>{t("aiTurn.noTools")}</div>
          )}
        </Field>
      ) : null}

      {metric && metric.repairs.length > 0 ? (
        <Field label={t("aiTurn.repairs")}>
          <Chips items={metric.repairs} />
        </Field>
      ) : null}

      {rounds.length > 0 ? (
        <div className={styles.field}>
          <button
            type="button"
            className={styles.historyToggle}
            aria-expanded={showTrace}
            onClick={toggleTrace}
          >
            {showTrace ? "▾" : "▸"} {t("aiTurn.trace")} ({rounds.length})
          </button>
          {traceTruncated ? (
            <div className={styles.muted}>{t("aiTurn.trace.truncated")}</div>
          ) : null}
          {showTrace ? (
            <div className={styles.section}>
              {rounds.map((round, i) => (
                <TraceRound key={`${round.round}-${i}`} round={round} />
              ))}
            </div>
          ) : null}
        </div>
      ) : traceTruncated ? (
        <Field label={t("aiTurn.trace")}>
          <div className={styles.muted}>{t("aiTurn.trace.truncated")}</div>
        </Field>
      ) : null}

      <Field label={t("aiTurn.lessons")}>
        {turn.lessons.length > 0 ? (
          <Chips items={turn.lessons.map((lesson) => lesson.title)} />
        ) : (
          <div className={styles.muted}>{t("aiTurn.noLessons")}</div>
        )}
      </Field>

      {turn.situationTags.length > 0 ? (
        <Field label={t("aiTurn.situations")}>
          <Chips
            items={turn.situationTags.map((tag) =>
              isLessonTag(tag) ? t(`aiLesson.tag.${tag}`) : tag
            )}
          />
          {turn.situationTagsSource === "recomputed_text_only" ? (
            <div className={styles.muted}>{t("aiTurn.situationsTextOnly")}</div>
          ) : null}
        </Field>
      ) : null}
    </>
  );
};

// Third column of the AI conversations page: the context of one AI reply and
// the form that turns its correction into a draft lesson.
const CorrectionPanel = ({
  conversationId,
  message,
  onClose,
  onUnavailable
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const turnMessageId = message.turnMessageId ?? null;

  // X is read-only, so it is cut to the server limit here.
  const [form, setForm] = useState<LessonFormValue>(() =>
    emptyLessonForm({
      badReply: toBadReplyText(message).slice(
        0,
        AI_LESSON_LIMITS.REPLY.MAX_LENGTH
      )
    })
  );
  const [warnings, setWarnings] = useState<AiLessonGuidanceIssue[]>([]);
  const [issues, setIssues] = useState<AiLessonGuidanceIssue[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [draftUnavailable, setDraftUnavailable] = useState(false);

  const {
    turn,
    isLoading: isLoadingTurn,
    isUnavailable: isTurnUnavailable,
    error: turnError
  } = useGetTurnDetail(conversationId, turnMessageId);

  useEffect(() => {
    if (isTurnUnavailable) onUnavailable?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTurnUnavailable]);

  const { assistants, isLoading: isLoadingAssistants } = useGetAssistants();
  const assistantId = useMemo(
    () =>
      assistants.find((a) => a.channel === EAiAssistantChannel.WhatsApp)?.id,
    [assistants]
  );

  const draftMutation = useDraftLesson(assistantId);
  const { createLesson } = useLessonMutations(assistantId ?? "");

  const turnErrorMessages = turnError
    ? parseLessonApiError(turnError).messages
    : [];

  const handleSuggest = () => {
    if (!assistantId || !turnMessageId || draftMutation.isLoading) return;
    draftMutation.mutate(
      {
        conversationId,
        turnMessageId,
        badReply: form.badReply || undefined,
        correction: form.correction.trim() || undefined
      },
      {
        onSuccess: (draft) => {
          setForm((current) =>
            applyDraftToForm(current, draft, new Date().toISOString())
          );
          setWarnings(draft.warnings ?? []);
          setIssues([]);
          setErrors([]);
        },
        onError: (error) => {
          if (parseLessonApiError(error).endpointMissing) {
            setDraftUnavailable(true);
            return;
          }
          toastServerError(error);
        }
      }
    );
  };

  const handleSave = () => {
    if (!assistantId || !turnMessageId || createLesson.isLoading) return;
    const formErrors = validateLessonForm(form);
    setIssues([]);
    setErrors(formErrors);
    if (formErrors.length > 0) return;

    createLesson.mutate(
      toCreateLessonBody(form, {
        conversationId,
        turnMessageId,
        messageId: message.id
      }),
      {
        onSuccess: () => {
          toast.success(t("aiLesson.toast.saved"));
          // The server marks this turn's "To review" item as handled: the
          // page and the sidebar badge follow.
          void queryClient.invalidateQueries({
            queryKey: [AI_REVIEW_QUERY_KEY]
          });
          onClose();
        },
        onError: (error) => {
          const details = parseLessonApiError(error);
          if (details.endpointMissing) {
            onUnavailable?.();
            return;
          }
          setIssues(details.issues);
          setErrors(
            details.messages.length > 0
              ? details.messages
              : ["aiTurn.panel.saveFailed"]
          );
        }
      }
    );
  };

  const variants = form.variants;

  const suggestSlot = draftUnavailable ? null : (
    <div className={styles.suggest}>
      <button
        type="button"
        className={styles.suggestButton}
        onClick={handleSuggest}
        disabled={!assistantId || draftMutation.isLoading}
      >
        {draftMutation.isLoading ? (
          <ClipLoader size={14} color="#7a5af8" />
        ) : (
          <SparkleIcon />
        )}
        <span>
          {draftMutation.isLoading
            ? t("aiLesson.form.suggesting")
            : t("aiLesson.form.suggest")}
        </span>
      </button>

      {variants ? (
        <div className={styles.variants}>
          {(["a", "b"] as const).map((which) => {
            const isChosen = variants.chosen === which;
            return (
              <div
                key={which}
                className={cn(styles.suggestion, {
                  [styles.suggestionChosen]: isChosen
                })}
              >
                <div className={styles.suggestionTitle}>
                  <SparkleIcon className={styles.suggestionTitleIcon} />
                  {t(
                    which === "a"
                      ? "aiLesson.form.versionA"
                      : "aiLesson.form.versionB"
                  )}
                </div>
                <p className={styles.suggestionText}>{variants[which]}</p>
                <div className={styles.suggestionActions}>
                  <button
                    type="button"
                    className={styles.suggestionApply}
                    disabled={isChosen}
                    onClick={() =>
                      setForm((current) => chooseLessonVariant(current, which))
                    }
                  >
                    {isChosen
                      ? t("aiLesson.form.chosen")
                      : t("aiLesson.form.useVersion")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.title}>{t("aiTurn.panel.title")}</div>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t("aiTurn.panel.close")}
          title={t("aiTurn.panel.close")}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.body}>
        <section className={styles.section}>
          <div className={styles.sectionTitle}>{t("aiTurn.panel.context")}</div>
          {isLoadingTurn ? (
            <div className={styles.loader}>
              <ClipLoader size={20} color="#fb3973" />
            </div>
          ) : turn ? (
            <TurnContext turn={turn} messageId={message.id} />
          ) : turnError && !isTurnUnavailable ? (
            <div className={styles.errorBox}>
              {(turnErrorMessages.length > 0
                ? turnErrorMessages
                : ["aiTurn.panel.loadFailed"]
              ).map((m) => (
                <div key={m}>{t(m)}</div>
              ))}
            </div>
          ) : null}
        </section>

        <section className={styles.section}>
          <LessonForm
            mode="create"
            value={form}
            onChange={setForm}
            issues={issues}
            warnings={warnings}
            disabled={createLesson.isLoading}
            suggestSlot={suggestSlot}
          />
        </section>
      </div>

      <div className={styles.footer}>
        {!isLoadingAssistants && !assistantId ? (
          <div className={styles.errorBox}>{t("aiTurn.panel.noAssistant")}</div>
        ) : null}
        {errors.length > 0 ? (
          <div className={styles.errorBox}>
            {errors.map((m) => (
              <div key={m}>{t(m)}</div>
            ))}
          </div>
        ) : null}
        <button
          type="button"
          className={styles.save}
          onClick={handleSave}
          disabled={!assistantId || createLesson.isLoading}
        >
          {createLesson.isLoading ? (
            <ClipLoader size={14} color="#fff" />
          ) : null}
          <span>{t("aiLesson.form.save")}</span>
        </button>
      </div>
    </div>
  );
};

export default CorrectionPanel;
